import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

/**
 * A project's place link, normalized for display (Phase 5 M5.7: sourced from
 * project_places → places → gis_locations; the frozen project_locations table
 * is no longer read). `location_type` carries the project_places ROLE
 * (venue/event/rehearsal/office/production/storage/other) to keep the legacy
 * consumer shape stable.
 */
export interface ProjectLocation {
  id: string;
  location_type: string;
  is_primary: boolean;
  notes?: string;
  /** The linked place's identity (new in M5.7). */
  place?: { id: string; code: string; name: string } | null;
  gis_location: {
    id: string;
    formatted_address: string;
    street_number?: string;
    street_name?: string;
    city?: string;
    state_province?: string;
    postal_code?: string;
    country?: string;
    country_code?: string;
    latitude: string;
    longitude: string;
    confidence_score?: string;
    is_validated?: boolean;
  };
}

export interface Project {
  id: string;
  code?: string;
  name: string;
  description: string | null;
  status: string;
  start_date: string | null;
  end_date: string | null;
  budget: number | null;
  team_size: number | null;
  sustainability_score?: number | null;
  carbon_footprint_level?: string | null;
  local_sourcing_percentage?: number | null;
  sustainability_notes?: string | null;
  organization_id: string;
  project_type_id?: string | null;
  project_type?: { id: string; name: string } | null;
  artistic_director_id?: string | null;
  created_at: string;
  updated_at: string;
  created_by: string;
  locations?: ProjectLocation[];
  primary_address?: string;
  // Computed in ProjectsList by joining useEntityCarbonEmissions().byProject —
  // not selected from the DB.
  total_emissions?: number;
}

export const useProjects = (organizationId?: string, includeLocations = false) => {
  return useQuery({
    queryKey: ['projects', organizationId, includeLocations],
    queryFn: async () => {
      if (!organizationId) return [];

      const query = supabase
        .from('projects')
        .select(includeLocations ? `
          *,
          project_type:project_types(id, name),
          place_links:project_places(
            id,
            role,
            is_primary,
            is_active,
            notes,
            place:places(
              id,
              code,
              name,
              gis_location:gis_locations(
                id,
                formatted_address,
                street_number,
                street_name,
                city,
                state_province,
                postal_code,
                country,
                country_code,
                latitude,
                longitude,
                confidence_score,
                is_validated
              )
            )
          )
        ` : `
          *,
          project_type:project_types(id, name)
        `)
        .eq('organization_id', organizationId)
        .order('name');

      const { data, error } = await query;

      if (error) throw error;

      // Normalize project_places links into the stable ProjectLocation shape
      // (role → location_type; a place without an address yields no map pin
      // but still lists) and compute primary_address.
      if (includeLocations && data) {
        type RawLink = {
          id: string;
          role: string;
          is_primary: boolean;
          is_active: boolean;
          notes: string | null;
          place: {
            id: string;
            code: string;
            name: string;
            gis_location: ProjectLocation['gis_location'] | null;
          } | null;
        };
        // The select string exceeds the PostgREST type-parser's depth, so
        // `data` degrades to ParserError — bridge through unknown.
        return (data as unknown as Array<Project & { place_links?: RawLink[] }>).map((project) => {
          const locations: ProjectLocation[] = (project.place_links ?? [])
            .filter((l) => l.is_active)
            .map((l) => ({
              id: l.id,
              location_type: l.role,
              is_primary: l.is_primary,
              notes: l.notes ?? undefined,
              place: l.place
                ? { id: l.place.id, code: l.place.code, name: l.place.name }
                : null,
              gis_location: l.place?.gis_location as ProjectLocation['gis_location'],
            }));
          const { place_links: _links, ...rest } = project;
          return {
            ...rest,
            locations,
            primary_address:
              locations.find((l) => l.is_primary)?.gis_location?.formatted_address ||
              locations[0]?.gis_location?.formatted_address ||
              '',
          };
        });
      }

      return data as unknown as Project[];
    },
    // Reference data (CLAUDE.md): avoid a refetch on every consumer mount.
    staleTime: 5 * 60 * 1000,
    enabled: !!organizationId,
  });
};